import { useCreatorBulkUpload } from '@round/creatorbase-helpers/api-hooks';
import { Empty, lucideIcons } from '@round/creatorbase-ui';
import BulkUploadCard from '../../../BulkUploadCard/BulkUploadCard';
import styles from './BulkUploadSummary.module.css';

const { AlertTriangle } = lucideIcons;

type Props = {
    bulkUploadId: number;
};

/** The card of the upload whose results are showing, as it reads in the list it was picked from. */
const BulkUploadSummary = ({ bulkUploadId }: Props) => {
    // An upload can finish processing while its cached results are still fresh, and the screen does not
    // poll, so opening it again always asks for where the upload has got to.
    const { upload, isLoading, isError, errorMessage } = useCreatorBulkUpload(bulkUploadId, {
        refetchOnMount: 'always',
    });

    if (isLoading) {
        return <BulkUploadCard.Skeleton />;
    }

    // Checked ahead of the upload itself: a refetch that fails keeps the cached upload alongside the error,
    // and an upload that is gone should not go on showing its old counts.
    if (isError || !upload) {
        return (
            <Empty className={styles.error}>
                <Empty.Header className={styles.errorHeader}>
                    <Empty.Media variant="icon" className={styles.errorIcon}>
                        <AlertTriangle />
                    </Empty.Media>
                    <Empty.Title>Couldn't load this upload's summary</Empty.Title>
                    {errorMessage && <Empty.Description>{errorMessage}</Empty.Description>}
                </Empty.Header>
            </Empty>
        );
    }

    return <BulkUploadCard upload={upload} />;
};

export default BulkUploadSummary;
